import { useParams } from "react-router-dom";
import { t } from "@/i18n-js/instance";
import { SomethingWentWrong } from "@/react/components/shared/ErrorPages/SomethingWentWrong";
import { withErrorBoundary } from "@/react/components/shared/ErrorPages/WithErrorBoundary";
import { userPaths } from "@/react/helpers/urlHelpers";
import { ProfileV3 } from "@circle-react/components/ProfileModalV3/Profile";
import { NotFoundError } from "@circle-react/config/CustomErrors";
import { usePunditUserContext } from "@circle-react/contexts";
import { isPrivateCommunity } from "@circle-react/helpers/communityHelpers";
import { PROFILE_PAGES } from "@circle-react/helpers/memberHelper";
import { useProfileInfo } from "@circle-react/hooks/profile/useProfileInfo";
import { useSmScreenMediaQuery } from "@circle-react/hooks/useMediaQuery";
import { PageMetaData } from "../Layout/PageMetaData";
import { StandardLayoutV2 } from "../Layout/StandardLayoutV2";
import { LoaderRow } from "../shared/LoaderRow";

interface Params {
  publicUid?: string;
}

const Component = () => {
  const { publicUid } = useParams<Params>();

  const {
    getProfileInfoQuery: {
      data: member,
      isLoading: isProfileLoading,
      isError,
      error,
    },
  } = useProfileInfo({
    publicId: publicUid,
    params: {
      page_name: PROFILE_PAGES.PROFILE_VIEW,
    },
  });
  if (isError && error?.status === 404) {
    throw new NotFoundError({ message: error?.message || "Profile not found" });
  }
  const {
    currentCommunity,
    currentUser,
    currentCommunityMember,
    isLoading: isCommunityLoading,
  } = usePunditUserContext();
  const isSmallScreen = useSmScreenMediaQuery();
  if (isProfileLoading || isCommunityLoading) {
    return <LoaderRow />;
  }

  if (isPrivateCommunity(currentCommunity)) {
    //Check if user is signed in
    if (!currentUser) {
      //Redirect to sign in page
      window.location.href = userPaths.signIn({
        params: {
          post_login_redirect: window.location.href,
        },
      });
      return <div>{t("redirecting_sign_in")}</div>;
    }
    //Check if user belongs to private community
    if (!currentCommunityMember) {
      window.location.href = "/"; //Automatically redirects to no access page.
      return <div>{t("accessing_private_community")}</div>;
    }
  }

  if (!member) {
    window.location.href = "/";
  }

  return (
    <>
      <PageMetaData />
      <StandardLayoutV2.Breadcrumb />
      <StandardLayoutV2.Content fullWidth fullHeight noPadding={isSmallScreen}>
        <div
          className="bg-primary flex w-full flex-col items-stretch md:h-[calc(100vh-64px)] md:flex-row"
          data-testid="profile-full-page"
        >
          <ProfileV3
            member={member}
            isLoading={isProfileLoading}
            isFullPageView
          />
        </div>
      </StandardLayoutV2.Content>
    </>
  );
};

export const ProfileFullPage = withErrorBoundary(
  Component,
  SomethingWentWrong,
  {
    handleNotFoundError: true,
  },
);

export default ProfileFullPage;
